import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useSiteConfig } from "@/contexts/SiteConfigContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { COPY_GROUPS } from "@/lib/copy";

export default function AdminSiteConfig() {
  const { config, refresh } = useSiteConfig();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (config) setForm(structuredClone(config));
  }, [config]);

  if (!form) return null;

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setTheme = (k, v) => setForm((f) => ({ ...f, theme: { ...f.theme, [k]: v } }));

  const addSocial = () =>
    setForm((f) => ({
      ...f,
      social_links: [...(f.social_links || []), { label: "", url: "", icon: "Github" }],
    }));
  const removeSocial = (i) =>
    setForm((f) => ({ ...f, social_links: f.social_links.filter((_, idx) => idx !== i) }));
  const updateSocial = (i, k, v) =>
    setForm((f) => ({
      ...f,
      social_links: f.social_links.map((s, idx) => (idx === i ? { ...s, [k]: v } : s)),
    }));

  const onSave = async () => {
    setSaving(true);
    try {
      await api.put("/site-config", form);
      await refresh();
      toast.success("Site config saved.");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <header className="mb-8 flex items-baseline justify-between">
        <div>
          <p className="label-xs">Site Config</p>
          <h1 className="mt-2 font-heading font-black text-4xl tracking-tighter">Content & Identity</h1>
        </div>
        <Button onClick={onSave} disabled={saving} data-testid="site-save">
          {saving ? "Saving…" : "Save changes"}
        </Button>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card title="Identity">
          <FF label="Name" v={form.name} on={(v) => setField("name", v)} tid="cfg-name" />
          <FF label="Role" v={form.role} on={(v) => setField("role", v)} tid="cfg-role" />
          <FF label="Years experience" type="number" v={form.years_experience} on={(v) => setField("years_experience", Number(v))} />
          <FF label="Location" v={form.location} on={(v) => setField("location", v)} />
          <FF label="Contact email" type="email" v={form.email} on={(v) => setField("email", v)} tid="cfg-email" />
          <FF label="Resume URL" v={form.resume_url || ""} on={(v) => setField("resume_url", v)} />
          <FF label="Profile image URL" v={form.profile_image_url || ""} on={(v) => setField("profile_image_url", v)} placeholder="https://… or /profile.jpg" />
          <FT label="Tagline" v={form.tagline} on={(v) => setField("tagline", v)} />
          <FT label="Bio" v={form.bio} on={(v) => setField("bio", v)} rows={5} />
        </Card>

        <Card title="Hero (Landing page)">
          <p className="text-xs text-muted-foreground">The headline itself is under Page copy → Home.</p>
          <FT label="Sub-headline" v={form.hero_sub} on={(v) => setField("hero_sub", v)} rows={3} />
          <div className="grid grid-cols-2 gap-3">
            <FF label="Primary CTA label" v={form.hero_cta_primary_label} on={(v) => setField("hero_cta_primary_label", v)} />
            <FF label="Primary CTA href" v={form.hero_cta_primary_href} on={(v) => setField("hero_cta_primary_href", v)} />
            <FF label="Secondary CTA label" v={form.hero_cta_secondary_label} on={(v) => setField("hero_cta_secondary_label", v)} />
            <FF label="Secondary CTA href" v={form.hero_cta_secondary_href} on={(v) => setField("hero_cta_secondary_href", v)} />
          </div>
        </Card>

        <Card title="Theme">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="label-xs">Accent color</Label>
              <div className="flex items-center gap-2 mt-2">
                <input type="color" value={form.theme.accent} onChange={(e) => setTheme("accent", e.target.value)} className="h-9 w-14 border border-border" />
                <Input value={form.theme.accent} onChange={(e) => setTheme("accent", e.target.value)} data-testid="cfg-accent" />
              </div>
            </div>
            <div>
              <Label className="label-xs">Accent (admin dark mode)</Label>
              <div className="flex items-center gap-2 mt-2">
                <input type="color" value={form.theme.accent_dark} onChange={(e) => setTheme("accent_dark", e.target.value)} className="h-9 w-14 border border-border" />
                <Input value={form.theme.accent_dark} onChange={(e) => setTheme("accent_dark", e.target.value)} />
              </div>
            </div>
          </div>
        </Card>

        <Card title="About page">
          <ListEditor
            label="Working principles"
            items={form.setup_notes || []}
            blank={{ knob: "", title: "", desc: "" }}
            cols={[["knob", "Tag", 3], ["title", "Title", 3], ["desc", "Description", 5]]}
            onChange={(v) => setField("setup_notes", v)}
          />
          <ListEditor
            label="Hobbies"
            items={form.workbench || []}
            blank={{ name: "", spec: "", image_url: "" }}
            cols={[["name", "Name", 3], ["spec", "Details", 4], ["image_url", "Photo URL", 4]]}
            onChange={(v) => setField("workbench", v)}
          />
        </Card>

        <Card title="Social links">
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="label-xs">Social links</Label>
              <Button size="sm" variant="ghost" onClick={addSocial} data-testid="cfg-add-social">
                <Plus className="h-4 w-4" /> Add
              </Button>
            </div>
            <div className="space-y-2">
              {(form.social_links || []).map((s, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-center">
                  <Input className="col-span-3" placeholder="Label" value={s.label} onChange={(e) => updateSocial(i, "label", e.target.value)} />
                  <Input className="col-span-6" placeholder="https://" value={s.url} onChange={(e) => updateSocial(i, "url", e.target.value)} />
                  <Input className="col-span-2" placeholder="Icon" value={s.icon || ""} onChange={(e) => updateSocial(i, "icon", e.target.value)} />
                  <Button size="icon" variant="ghost" className="col-span-1" onClick={() => removeSocial(i)}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          </div>
        </Card>
      </div>

      <section className="mt-6 border border-border rounded-sm p-5">
        <div className="flex items-baseline justify-between gap-4 mb-4">
          <h2 className="font-heading font-bold text-lg tracking-tight">Page copy</h2>
          <p className="text-xs text-muted-foreground">Every fixed line on the public site. Leave a field blank to use the default shown.</p>
        </div>
        <div className="space-y-2">
          {COPY_GROUPS.map((g) => (
            <details key={g.group} className="border border-border rounded-sm">
              <summary className="cursor-pointer select-none px-4 py-3 text-sm font-medium">{g.group}</summary>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 p-4 pt-1">
                {g.items.map(([key, label, def, multi]) => {
                  const val = form.page_copy?.[key] ?? "";
                  const set = (v) => setForm((f) => ({ ...f, page_copy: { ...(f.page_copy || {}), [key]: v } }));
                  return (
                    <div key={key} className={multi ? "lg:col-span-2" : undefined}>
                      <Label className="label-xs">{label}</Label>
                      {multi ? (
                        <Textarea rows={2} value={val} placeholder={def} onChange={(e) => set(e.target.value)} className="mt-2" />
                      ) : (
                        <Input value={val} placeholder={def} onChange={(e) => set(e.target.value)} className="mt-2" />
                      )}
                    </div>
                  );
                })}
              </div>
            </details>
          ))}
        </div>
      </section>
    </div>
  );
}

const SPAN = { 3: "col-span-3", 4: "col-span-4", 5: "col-span-5" };

function ListEditor({ label, items, blank, cols, onChange }) {
  const update = (i, k, v) => onChange(items.map((it, idx) => (idx === i ? { ...it, [k]: v } : it)));
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <Label className="label-xs">{label}</Label>
        <Button size="sm" variant="ghost" onClick={() => onChange([...items, { ...blank }])}>
          <Plus className="h-4 w-4" /> Add
        </Button>
      </div>
      <div className="space-y-2">
        {items.map((it, i) => (
          <div key={i} className="grid grid-cols-12 gap-2 items-center">
            {cols.map(([k, ph, span]) => (
              <Input key={k} className={SPAN[span]} placeholder={ph} value={it[k] || ""} onChange={(e) => update(i, k, e.target.value)} />
            ))}
            <Button size="icon" variant="ghost" className="col-span-1" onClick={() => onChange(items.filter((_, idx) => idx !== i))}>
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
}

function Card({ title, children }) {
  return (
    <section className="border border-border rounded-sm p-5">
      <h2 className="font-heading font-bold text-lg tracking-tight mb-4">{title}</h2>
      <div className="space-y-4">{children}</div>
    </section>
  );
}
function FF({ label, v, on, type = "text", tid, placeholder }) {
  return (
    <div>
      <Label className="label-xs">{label}</Label>
      <Input type={type} value={v ?? ""} onChange={(e) => on(e.target.value)} className="mt-2" data-testid={tid} placeholder={placeholder} />
    </div>
  );
}
function FT({ label, v, on, rows = 4 }) {
  return (
    <div>
      <Label className="label-xs">{label}</Label>
      <Textarea rows={rows} value={v ?? ""} onChange={(e) => on(e.target.value)} className="mt-2" />
    </div>
  );
}
